"use client";

import type { Organization } from "~care/types";
import type { CareGiverLeadFormData } from "~care/types/forms/care-giver-lead-form";
import type { JobSelectionState } from "./JobSection";
import type { LocationSelectionState } from "./LocationSection";

import { track } from "@homeinstead/analytics";
import { useNearbyLocations } from "@homeinstead/geolocation/hooks";
import { EventName } from "@homeinstead/shared";
import { cn } from "@homeinstead/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useActionState, useMemo, useState } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { submitCareGiverForm } from "~care/app/actions/submitCareGiverForm";
import { Button } from "~care/components/ui/Button/Button";
import { Input } from "~care/components/ui/Input/Input";
import { useLocationCareers } from "~care/hooks/useLocationCareers";
import { CareerFormSubmitEvent } from "~care/types/events";
import { careGiverLeadFormSchema } from "~care/types/forms/care-giver-lead-form";
import { FormError } from "../../FormError/FormError";
import { Link } from "../../Link/Link";
import { PhoneInput } from "../../PhoneInput/PhoneInput";
import { Checkbox } from "../Checkbox";
import { checkboxFields, textFields } from "./fields";
import { JobSection } from "./JobSection";
import { LocationSection } from "./LocationSection";
import { CareGiverLeadFormThankYou } from "./ThankYou";

interface CareGiverLeadFormProps
  extends React.FormHTMLAttributes<HTMLFormElement> {
  isLastStep: boolean;
  handleNext: () => void;
  // Pass franchise to the form if used in a local lander and franchise is known ahead of time.
  // TODO: Pull franchise type from sanity types once that work is merged. Pick<> the fields needed.
  franchise?: {
    id: string;
    location: {
      name: string;
      postalCode: string;
    };
  };
}

const CareGiverLeadForm = ({
  className,
  isLastStep,
  handleNext,
  franchise,
  ...props
}: CareGiverLeadFormProps) => {
  const methods = useForm<CareGiverLeadFormData>({
    defaultValues: {
      step1: { Role: "caregiver" },
      step2: {
        BusinessInEnglish: false,
        CanWorkInUS: false,
        ConsentTerms: false,
        Email: "",
        FirstName: "",
        jobID: "",
        LastName: "",
        OptInEmail: true,
        OptInSms: false,
        organizationNumber: franchise?.id ? Number(franchise?.id) : 0,
        Phone: "",
        postalCode: franchise?.location.postalCode ?? "",
        userEnteredLocation: franchise?.location.postalCode ?? "",
      },
    },
    mode: "onBlur",
    resolver: zodResolver(careGiverLeadFormSchema),
  });

  const {
    register,
    control,
    formState: { errors, isValid },
  } = methods;

  const officeNumber = useWatch({ control, name: "step2.organizationNumber" });
  const userEnteredLocation = useWatch({
    control,
    name: "step2.userEnteredLocation",
  });
  const postalCode = useWatch({ control, name: "step2.postalCode" });

  const {
    data: nearbyLocations,
    isFetching: isFetchingNearbyLocations,
    error: errorOnNearbyLocations,
  } = useNearbyLocations({
    config: { apiEndpoint: "/api/geo-search" },
    enabled: Boolean(userEnteredLocation),
    location: userEnteredLocation,
  });

  const franchiseOptions: Organization[] = nearbyLocations?.organizations ?? [];

  const {
    data: locationCareers,
    isFetching: isFetchingLocationCareers,
    error: errorOnLocationCareers,
  } = useLocationCareers({ officeNumber });

  const [locationSelectOpen, setLocationSelectOpen] = useState(false);
  const [jobSelectOpen, setJobSelectOpen] = useState(false);

  const hasLocalFranchise = Boolean(franchise);

  const locationSelectionState: LocationSelectionState = useMemo(() => {
    if (hasLocalFranchise) return "local";
    if (!userEnteredLocation) return "idle";
    if (isFetchingNearbyLocations) return "loading";
    if (errorOnNearbyLocations) return "error";
    if (franchiseOptions.length === 0) return "no-locations";
    return "has-locations";
  }, [
    hasLocalFranchise,
    userEnteredLocation,
    isFetchingNearbyLocations,
    errorOnNearbyLocations,
    franchiseOptions.length,
  ]);

  const jobSelectionState: JobSelectionState = useMemo(() => {
    // If the location selection isn't ready to show jobs, short‑circuit
    if (locationSelectionState !== "local") {
      if (locationSelectionState !== "has-locations") return "idle";

      if (!officeNumber) return "idle";
      if (isFetchingLocationCareers) return "loading";
      if (errorOnLocationCareers) return "error";
    }

    if (!locationCareers) {
      return "idle";
    }

    // Make sure there is at least one job in the array
    return locationCareers && locationCareers.length > 0
      ? "has-jobs"
      : "no-jobs";
  }, [
    locationSelectionState,
    officeNumber,
    isFetchingLocationCareers,
    errorOnLocationCareers,
    locationCareers,
  ]);

  // Close job dropdown whenever location becomes invalid
  React.useEffect(() => {
    if (locationSelectionState !== "has-locations") {
      setJobSelectOpen(false);
    }
    if (jobSelectionState === "no-jobs" || jobSelectionState === "error") {
      methods.setValue("step2.jobID", "", {
        shouldValidate: true,
      });
    }
  }, [locationSelectionState, jobSelectionState, methods]);

  const [state, submitFormAction, isPending] = useActionState(
    submitCareGiverForm,
    null,
  );

  if (state?.ok && !state?.data?.isError) {
    const redirectURL = state?.data?.jobApplicationURL;
    if (redirectURL) {
      track(
        EventName.CAREER_FORM_SUBMIT,
        CareerFormSubmitEvent({
          franchiseNumber: officeNumber.toString(),
          page_url: window.location.href,
          result: "success",
        }),
      );

      // defer redirect until next tick to let analytics fire
      setTimeout(() => (window.location.href = redirectURL), 30);

      return (
        <div className="p-8 text-center text-new-beginnings-base">
          <p className="font-semibold text-md">
            Redirecting you to the job application…
          </p>
        </div>
      );
    }
    return <CareGiverLeadFormThankYou />;
  }
  if (state?.ok && state?.data?.isError) {
    const isDuplicateError = state.data.errors?.some(
      (e: { code: string }) => e.code === "duplicate",
    );
    track(
      EventName.CAREER_FORM_SUBMIT,
      CareerFormSubmitEvent({
        franchiseNumber: officeNumber.toString(),
        page_url: window.location.href,
        result: isDuplicateError ? "duplicate" : "server-error",
      }),
    );
    return <CareGiverLeadFormThankYou />;
  } else if (state !== null && !state?.ok) {
    const validationErrors = state?.validationErrors;
    track(
      EventName.CAREER_FORM_SUBMIT,
      CareerFormSubmitEvent({
        franchiseNumber: officeNumber.toString(),
        page_url: window.location.href,
        result: validationErrors ? "validation-error" : "server-error",
      }),
    );
  }

  return (
    <FormProvider {...methods}>
      <form
        action={submitFormAction}
        className={cn("px-4 xs:px-12 py-4", className)}
        {...props}
      >
        <div className="flex w-full flex-col gap-4 md:w-[424px]">
          <h2 className="font-bold text-lg text-new-beginnings-base">
            Apply to be a caregiver
          </h2>

          <Input type="hidden" {...register("step1.Role")} value="caregiver" />

          {textFields.map(([field, label]) => {
            const name = `step2.${field}` as const;
            return (
              <div key={field}>
                <Input
                  id={field}
                  placeholder={label}
                  type={field === "Email" ? "email" : "text"}
                  {...register(name)}
                  className="bg-white text-base text-new-beginnings-base"
                />
                <FormError.span>
                  {errors.step2?.[field]?.message}
                </FormError.span>
              </div>
            );
          })}

          <PhoneInput<CareGiverLeadFormData>
            className="bg-white text-base text-new-beginnings-base"
            fieldName="step2.Phone"
          />

          {locationSelectionState === "local" ? (
            <>
              <input
                type="hidden"
                value={officeNumber}
                {...register("step2.organizationNumber")}
              />
              <input
                type="hidden"
                value={postalCode}
                {...register("step2.postalCode")}
              />
              <input
                type="hidden"
                value={userEnteredLocation}
                {...register("step2.userEnteredLocation")}
              />
            </>
          ) : (
            <LocationSection
              franchiseOptions={franchiseOptions}
              locationSelectionState={locationSelectionState}
              locationSelectOpen={locationSelectOpen}
              setLocationSelectOpen={setLocationSelectOpen}
            />
          )}

          <JobSection
            isOpen={jobSelectOpen}
            jobSelectionState={jobSelectionState}
            locationCareers={locationCareers}
            setIsOpen={setJobSelectOpen}
          />

          {checkboxFields.map(([field, label]) => {
            const name = `step2.${field}` as const;
            return (
              <Controller
                control={control}
                key={field}
                name={name}
                render={({ field: ctrl }) => (
                  <div className="flex items-start gap-2">
                    <Checkbox
                      checked={ctrl.value}
                      id={name}
                      name={name}
                      onChange={ctrl.onChange}
                    />
                    <label
                      className="text-new-beginnings-base text-xs"
                      htmlFor={name}
                    >
                      {label}
                      {field === "ConsentTerms" && (
                        <span>
                          {" "}
                          For more details, please refer to our{" "}
                          <Link
                            className="text-xs underline hover:no-underline"
                            href="https://www.homeinstead.com/legal/privacy-policy"
                            rel="noopener noreferrer"
                            size="inline"
                            target="_blank"
                            variant="plain"
                          >
                            Privacy Policy
                          </Link>
                          .
                        </span>
                      )}
                    </label>
                    {errors.step2?.[field] && (
                      <FormError.span>
                        {errors.step2[field]?.message}
                      </FormError.span>
                    )}
                  </div>
                )}
              />
            );
          })}
        </div>

        <Button
          className="mt-4 h-16 w-full rounded-full"
          disabled={isPending || !isValid}
          onClick={isLastStep ? undefined : handleNext}
          type={isLastStep ? "submit" : "button"}
        >
          {isLastStep ? "Submit" : "Next"}
        </Button>
      </form>
    </FormProvider>
  );
};

export default CareGiverLeadForm;
